import React, { useEffect, useMemo, useState } from 'react'
import { connect, useDispatch, useSelector } from 'react-redux'
import config from './config.js'

import redux from './redux/index.js'
import socket from './redux/socket-io.js'
import { makeSelectorContactInfo } from './redux/login/selectors.js'
import { selectLocale } from './redux/locale/selectors'

import { Logo } from '@pergas-common/pergas-icons'
import { TemplateIcon, ProjectIcon, ContactIcon } from './icons/index.js'

import { Topbar, styled } from '@pergas-common/pergas-components'
import { Menu, MenuItem } from '@mui/material'
import AccountCircle from '@mui/icons-material/AccountCircle'
import { SnackBarContextProvider } from './context/common.js'
import SnackBar from './components/SnackBar.js'
import Logout from './components/Logout.js'
import PdsSpinner from './components/PdsSpinner.js'
import NoAccount from './components/NoAccount.js'
import PaneHeader from './components/PaneHeader.js'
import { getGraphAccessToken } from './lib/msal.js'
import FileView from './components/FileView.js'
import SaveView from './components/SaveView.js'

import { isValidToken } from './util.js'

const APP_VERSION = process.env.REACT_APP_VERSION || ''

const ScrollStyle = {
  height: 'calc(100% - 50px)'
}

const Holder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: row;
`

const LeftPane = styled.div`
  width: 50%;
  overflow-y: scroll;
`

const RightPane = styled.div`
  width: 50%;
  overflow-y: scroll;

`

const BrandIdentity = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  > span {
    color: #fff;
    font-size: 12px;
    opacity: 0.9;
  }

`

const UserMenuButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #fff;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 8px 10px;

  &:hover {
    background: rgba(255, 255, 255, 0.12);
  }

  > span {
    font-size: 14px;
    max-width: 260px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  > img {
    object-fit: cover;
  }

`

const App = ({
  idToken,
  strings,
  token,
  branding,
  userEmail,
  doLogout,
  showNoAccountPage,
  connectWebsockets,
  doStartHeartbeat,
  doLogin
}) => {
  const [confirmLogout, setConfirmLogout] = useState(false)
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState(null)
  const [userPhotoUrl, setUserPhotoUrl] = useState('')
  const [hasPhotoError, setHasPhotoError] = useState(false)
  const userMenuOpen = Boolean(userMenuAnchorEl)
  const isLoggedIn = isValidToken(token)

  console.log('App branding', branding)

  // Start heartbeat on initial render.
  useEffect(doStartHeartbeat, [])

  // (Re-)Connect to websockets if token changed.
  useEffect(() => {
    if (isLoggedIn) {
      connectWebsockets(token)
    }
  }, [token])

  // Auto login if not logged in.
  useEffect(() => {
    if (!isLoggedIn) {
      setTimeout(() => doLogin(idToken), config.autoLoginTimeout)
    }
  }, [isLoggedIn])

  useEffect(() => {
    let objectUrl = null

    if (!isLoggedIn || !userEmail) {
      setUserPhotoUrl('')
      setHasPhotoError(false)
      return
    }

    ;(async () => {
      try {
        const graphToken = await getGraphAccessToken({ email: userEmail })
        const response = await fetch('https://graph.microsoft.com/v1.0/me/photo/$value', {
          headers: {
            Authorization: `Bearer ${graphToken}`
          }
        })

        if (!response.ok) {
          setUserPhotoUrl('')
          setHasPhotoError(false)
          return
        }

        const blob = await response.blob()
        objectUrl = URL.createObjectURL(blob)
        setHasPhotoError(false)
        setUserPhotoUrl(objectUrl)
      } catch (err) {
        console.warn('Failed to fetch user profile photo', err)
        setUserPhotoUrl('')
        setHasPhotoError(false)
      }
    })()

    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl)
      }
    }
  }, [isLoggedIn, userEmail])

  const background = branding?.headerBackground || 'rgb(32, 106, 133)'

  const renderBrandedLogo = (branding) => {
    return (
      <BrandIdentity>
        {branding?.headerIcon !== 'none' && <Logo width={32} height={32} fill='white' />}
        <span>{APP_VERSION}</span>
      </BrandIdentity>
    )
  }

  const onOpenUserMenu = (event) => {
    setUserMenuAnchorEl(event.currentTarget)
  }

  const onCloseUserMenu = () => {
    setUserMenuAnchorEl(null)
  }

  const onMenuLogout = () => {
    onCloseUserMenu()
    setConfirmLogout(true)
  }

  if (showNoAccountPage) {
    return <NoAccount />
  } else {
    return (
      <>
        {isLoggedIn
          ? (<>
            <div style={{ width: '100%', background }}>
              {confirmLogout && <Logout
                isOpen={confirmLogout} onSubmit={() => {
                  doLogout()
                  setConfirmLogout(false)
                }} onCancel={() => {
                  setConfirmLogout(false)
                }}
                                />}
              <Topbar
                left={renderBrandedLogo(branding)} right={
                  <>
                    <UserMenuButton type='button' onClick={onOpenUserMenu}>
                      {(userPhotoUrl && !hasPhotoError)
                        ? <img src={userPhotoUrl} alt='User profile' style={{ width: 24, height: 24, borderRadius: '50%' }} onError={() => { setHasPhotoError(true) }} />
                        : <AccountCircle style={{ width: 24, height: 24 }} />}
                      <span>{userEmail || ''}</span>
                    </UserMenuButton>
                    <Menu
                      anchorEl={userMenuAnchorEl}
                      open={userMenuOpen}
                      onClose={onCloseUserMenu}
                    >
                      <MenuItem onClick={onMenuLogout}>Logga ut</MenuItem>
                    </Menu>
                  </>
}
              />
            </div>
            <SnackBarContextProvider>
              <div style={ScrollStyle}>
                <SnackBar />
                <TmplViewer branding={branding} />
              </div>
            </SnackBarContextProvider>
          </> // eslint-disable-line
            )
          : <Spinner />}
      </>
    )
  }
}

const Spinner = () => {
  return (
    <div style={alignSpinnerStyle}>
      <PdsSpinner />
    </div>
  )
}

const TmplViewer = ({ branding }) => {
  console.log('TmplViewer branding', branding)
  const dispatch = useDispatch()
  const locale = useSelector(selectLocale)
  const selectUserdata = useMemo(makeSelectorContactInfo, [])
  const userData = useSelector((state) => selectUserdata(state))
  const urlParam = Object.fromEntries(new URLSearchParams(window.location.search).entries())

  const RightPaneHeaderIcon = urlParam.pds_type === 'contact' ? ContactIcon : ProjectIcon
  const rightPaneHeaderText = urlParam.pds_type === 'contact' ? locale.save_to_client : locale.save_to_project

  useEffect(() => {
    dispatch({
      type: 'SET_DOCUMENT_DATA_ACTION',
      key: 'me',
      value: userData
    })
  }, [dispatch, userData])

  return (
    <Holder>
      <LeftPane>
        <PaneHeader leftComponent={TemplateIcon} headerText={locale.templates_root_node} branding={branding} />
        <FileView {...urlParam} />
      </LeftPane>
      <RightPane>
        <PaneHeader leftComponent={RightPaneHeaderIcon} headerText={rightPaneHeaderText} branding={branding} />
        <SaveView />
      </RightPane>
    </Holder>
  )
}

const alignSpinnerStyle = {
  width: '100%',
  height: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center'
}

const mapStateToProps = (state) => {
  const { userData } = state.login
  const { token, email } = userData
  const branding = userData.tmSettings?.branding

  return {
    error: state.error,
    strings: state.locale.strings,
    showNoAccountPage: state.login.showNoAccountPage,
    token,
    userEmail: email,
    branding
  }
}

const mapDispatchToProps = (dispatch) => {
  const { error, login } = redux.actions
  return {
    doLogin: (idToken) => {
      dispatch(login.doLogin(idToken))
    },
    doLogout: () => {
      dispatch(redux.actions.login.doLogout())
    },
    doStartHeartbeat: () => {
      dispatch(redux.actions.login.doStartHeartbeat())
    },
    connectWebsockets: (token) => {
      socket.connect(token, dispatch)
    },
    onResetError: () => {
      dispatch(error.resetError())
    }
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(App)
